import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import { LoginPage } from './pages/auth/LoginPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { MpgGridPocPage } from './pages/poc/MpgGridPocPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { ProductListPage } from './pages/products/ProductListPage';
import { ProductFormPage } from './pages/products/ProductFormPage';
import { PackagingItemListPage } from './pages/packaging-items/PackagingItemListPage';
import { PackagingItemFormPage } from './pages/packaging-items/PackagingItemFormPage';
import { LotListPage } from './pages/lots/LotListPage';
import { LotFormPage } from './pages/lots/LotFormPage';
import { CustomerListPage } from './pages/customers/CustomerListPage';
import { CustomerFormPage } from './pages/customers/CustomerFormPage';
import { SupplierListPage } from './pages/suppliers/SupplierListPage';
import { SupplierFormPage } from './pages/suppliers/SupplierFormPage';
import { PscListPage } from './pages/psc/PscListPage';
import { PscFormPage } from './pages/psc/PscFormPage';
import { LnpListPage } from './pages/lnp/LnpListPage';
import { LnpFormPage } from './pages/lnp/LnpFormPage';
import { MpgListPage } from './pages/mpg/MpgListPage';
import { MpgFormPage } from './pages/mpg/MpgFormPage';
import { SpedizioniListPage } from './pages/spedizioni/SpedizioniListPage';
import { SpedizioneFormPage } from './pages/spedizioni/SpedizioneFormPage';
import { InvListPage } from './pages/inv/InvListPage';
import { InvFormPage } from './pages/inv/InvFormPage';
import { SapListPage } from './pages/sap/SapListPage';
import { SapFormPage } from './pages/sap/SapFormPage';
import { ConListPage } from './pages/con/ConListPage';
import { ConFormPage } from './pages/con/ConFormPage';
import { ComingSoonPage } from './pages/warehouse/ComingSoonPage';
import { StockMovementsListPage } from './pages/stock-movements/StockMovementsListPage';
import { UsersListPage } from './pages/users/UsersListPage';
import { OperatorsListPage } from './pages/operators/OperatorsListPage';
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage';
import { RequireAuth } from './lib/RequireAuth';
import { AppShell } from './components/AppShell';
import { Warehouse, Factory, Truck } from 'lucide-react';

// Back-compat: vecchi deep-link MSS/MSF -> modulo unico Spedizioni (preserva l'id).
function SpedizioneEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/documents/spedizioni/${id}/edit`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/products" element={<ProductListPage />} />
        <Route path="/products/new" element={<ProductFormPage />} />
        <Route path="/products/:id/edit" element={<ProductFormPage />} />
        <Route path="/packaging-items" element={<PackagingItemListPage />} />
        <Route path="/packaging-items/new" element={<PackagingItemFormPage />} />
        <Route path="/packaging-items/:id/edit" element={<PackagingItemFormPage />} />
        <Route path="/lots" element={<LotListPage />} />
        <Route path="/lots/new" element={<LotFormPage />} />
        <Route path="/lots/:id/edit" element={<LotFormPage />} />
        <Route path="/customers" element={<CustomerListPage />} />
        <Route path="/customers/new" element={<CustomerFormPage />} />
        <Route path="/customers/:id/edit" element={<CustomerFormPage />} />
        <Route path="/suppliers" element={<SupplierListPage />} />
        <Route path="/suppliers/new" element={<SupplierFormPage />} />
        <Route path="/suppliers/:id/edit" element={<SupplierFormPage />} />
        <Route path="/documents/psc" element={<PscListPage />} />
        <Route path="/documents/psc/new" element={<PscFormPage />} />
        <Route path="/documents/psc/:id/edit" element={<PscFormPage />} />
        <Route path="/documents/lnp" element={<LnpListPage />} />
        <Route path="/documents/lnp/new" element={<LnpFormPage />} />
        <Route path="/documents/lnp/:id/edit" element={<LnpFormPage />} />
        <Route path="/documents/mpg" element={<MpgListPage />} />
        <Route path="/documents/mpg/:id/edit" element={<MpgFormPage />} />
        <Route path="/documents/spedizioni" element={<SpedizioniListPage />} />
        <Route path="/documents/spedizioni/new" element={<SpedizioneFormPage />} />
        <Route path="/documents/spedizioni/:id/edit" element={<SpedizioneFormPage />} />
        {/* Redirect back-compat dai vecchi moduli separati MSS (Spagna) / MSF (Francia) */}
        <Route path="/documents/mss" element={<Navigate to="/documents/spedizioni" replace />} />
        <Route path="/documents/mss/new" element={<Navigate to="/documents/spedizioni/new" replace />} />
        <Route path="/documents/mss/:id/edit" element={<SpedizioneEditRedirect />} />
        <Route path="/documents/msf" element={<Navigate to="/documents/spedizioni" replace />} />
        <Route path="/documents/msf/new" element={<Navigate to="/documents/spedizioni/new" replace />} />
        <Route path="/documents/msf/:id/edit" element={<SpedizioneEditRedirect />} />
        <Route path="/documents/inv" element={<InvListPage />} />
        <Route path="/documents/inv/new" element={<InvFormPage />} />
        <Route path="/documents/inv/:id/edit" element={<InvFormPage />} />
        <Route path="/documents/sap" element={<SapListPage />} />
        <Route path="/documents/sap/new" element={<SapFormPage />} />
        <Route path="/documents/sap/:id/edit" element={<SapFormPage />} />
        <Route path="/documents/con" element={<ConListPage />} />
        <Route path="/documents/con/:id/edit" element={<ConFormPage />} />
        <Route
          path="/warehouse/main"
          element={<ComingSoonPage titleKey="nav.warehouseMain" icon={Warehouse} />}
        />
        <Route
          path="/warehouse/production"
          element={<ComingSoonPage titleKey="nav.warehouseProduction" icon={Factory} />}
        />
        <Route
          path="/warehouse/shipping"
          element={<ComingSoonPage titleKey="nav.warehouseShipping" icon={Truck} />}
        />
        <Route path="/stock-movements" element={<StockMovementsListPage />} />
        <Route path="/poc/mpg-grid" element={<MpgGridPocPage />} />
        <Route path="/admin/users" element={<UsersListPage />} />
        <Route path="/admin/operators" element={<OperatorsListPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
